'use client';

import { useEffect, useState, useCallback } from 'react';
import { toast } from 'sonner';
import { isElectron, chat } from '@/lib/api';

interface DialogRequest {
  type: 'ui:confirm' | 'ui:select' | 'ui:input';
  requestId: string;
  title: string;
  message?: string;
  options?: string[];
  placeholder?: string;
}

// The same toast repeating forever is worse than no toast: sessions doing
// visual work cycle screenshot → "N images, auto-compacting" → compact →
// screenshot again, re-firing the identical warning for hours (Boss: "those
// notifications pop up continuously, the same ones in loop"). Digits are
// normalized so "10 images (~200K)" and "14 images (~280K)" count as the same
// message, and a message class toasts at most once per window.
const DEDUPE_MS = 10 * 60_000;
const lastToastAt = new Map<string, number>();
function isDuplicateToast(level: string, msg: string): boolean {
  const key = `${level}:${msg.replace(/\d+/g, '#')}`;
  const now = Date.now();
  const last = lastToastAt.get(key) || 0;
  if (now - last < DEDUPE_MS) return true;
  lastToastAt.set(key, now);
  if (lastToastAt.size > 200) {
    for (const [k, t] of lastToastAt) if (now - t > DEDUPE_MS) lastToastAt.delete(k);
  }
  return false;
}

export function SomaDialogProvider() {
  const [dialog, setDialog] = useState<DialogRequest | null>(null);
  const [inputValue, setInputValue] = useState('');

  const respond = useCallback((result: any) => {
    if (!dialog || !isElectron) return;
    window.beast!.invoke('ui:dialog:respond', { requestId: dialog.requestId, result });
    setDialog(null);
    setInputValue('');
  }, [dialog]);

  useEffect(() => {
    if (!isElectron) return;

    const unsub = chat.onEvent((event: any) => {
      if (event.type === 'ui:notify') {
        const level = event.level || 'info';
        const msg = String(event.message || '');
        // Filter out statusline operational noise — in Cursor these go to the
        // footer bar, not popups. In Beast we don't have a footer yet.
        const lower = msg.toLowerCase();
        if (lower.includes('keepalive') || lower.includes('cache')
          || lower.includes('soul-space') || lower.includes('context:')
          || lower.includes('context ') && level === 'info'
          // idle/zombie shutdown warnings: the session's exit is neutralized by
          // the host (gateway exit-guard / Electron), so the session never
          // actually dies — these toasts are pure noise. Drop them.
          || lower.includes('shutting down') || lower.includes('no user input for')
          // image-budget housekeeping: sessions doing visual work auto-compact
          // on a cycle — automatic, self-resolving, and it re-fires for hours.
          // Operational noise, same class as keepalive/cache above.
          || lower.includes('images in context') || lower.includes('screenshots in context')
          || lower.includes('image budget') || lower.includes('auto-compact')
          || msg.includes('♥') || msg.includes('💤') || msg.includes('🌀')
          || msg.includes('🔄 Restart required')) return;
        // Anything that survives the noise filter still only toasts once per
        // window — a legitimate warning repeating in a loop adds nothing new.
        if (isDuplicateToast(level, msg)) return;
        if (level === 'error') toast.error(msg);
        else if (level === 'warning') toast.warning(msg);
        else toast.info(msg);
      }

      if (event.type === 'ui:confirm' || event.type === 'ui:select' || event.type === 'ui:input') {
        setDialog(event as DialogRequest);
        setInputValue('');
      }
    });

    return unsub;
  }, []);

  if (!dialog) return null;

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/60"
      onClick={() => respond(dialog.type === 'ui:confirm' ? false : undefined)}>
      <div className="bg-[#0d1117]/85 border border-[rgb(var(--border))] rounded-xl p-6 max-w-md w-full mx-4 shadow-2xl"
        onClick={e => e.stopPropagation()}>
        <h3 className="text-lg font-semibold text-white mb-2">{dialog.title}</h3>

        {dialog.type === 'ui:confirm' && (
          <>
            <p className="text-gray-400 text-sm mb-6">{dialog.message}</p>
            <div className="flex gap-3 justify-end">
              <button onClick={() => respond(false)}
                className="px-4 py-2 rounded-lg bg-[rgb(var(--muted))] text-gray-400 hover:text-white transition-colors">
                Cancel
              </button>
              <button onClick={() => respond(true)}
                className="px-4 py-2 rounded-lg bg-cyan-500/20 border border-cyan-500/30 text-cyan-400 hover:bg-cyan-500/30 transition-colors">
                Confirm
              </button>
            </div>
          </>
        )}

        {dialog.type === 'ui:select' && (
          <div className="flex flex-col gap-2 mt-4">
            {dialog.options?.map(opt => (
              <button key={opt} onClick={() => respond(opt)}
                className="text-left px-4 py-3 rounded-lg bg-[rgb(var(--muted))]/50 text-gray-300 hover:bg-cyan-500/10 hover:text-cyan-400 border border-[rgb(var(--border))]/40 transition-colors">
                {opt}
              </button>
            ))}
            <button onClick={() => respond(undefined)}
              className="text-sm text-gray-600 mt-2 hover:text-gray-400 transition-colors">
              Cancel
            </button>
          </div>
        )}

        {dialog.type === 'ui:input' && (
          <form onSubmit={e => { e.preventDefault(); respond(inputValue); }}>
            <input
              autoFocus
              value={inputValue}
              onChange={e => setInputValue(e.target.value)}
              placeholder={dialog.placeholder}
              className="w-full px-4 py-2.5 rounded-lg bg-[rgb(var(--muted))]/40 border border-[rgb(var(--border))]/40 text-white mt-4 mb-4 outline-none focus:border-cyan-500/40 focus:ring-1 focus:ring-cyan-500/20 text-sm"
            />
            <div className="flex gap-3 justify-end">
              <button type="button" onClick={() => respond(undefined)}
                className="px-4 py-2 rounded-lg bg-[rgb(var(--muted))] text-gray-400 hover:text-white transition-colors">
                Cancel
              </button>
              <button type="submit"
                className="px-4 py-2 rounded-lg bg-cyan-500/20 border border-cyan-500/30 text-cyan-400 hover:bg-cyan-500/30 transition-colors">
                Submit
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
